import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import './EntityRequestManager.css';

const EntityRequestManager = ({ user }) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    loadPendingRequests();
  }, []);

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/entity-join/pending');
      
      if (response.data.success) {
        setRequests(response.data.requests);
      } else {
        setError(response.data.error);
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      setError(t('entityRequestManager.errors.loadRequests'));
    } finally {
      setLoading(false);
    }
  };

  const handleValidation = async (requestId, action, comment = '') => {
    try {
      setProcessingId(requestId);
      
      const response = await apiClient.post(`/api/entity-join/validate/${requestId}`, {
        action,
        comment
      });

      if (response.data.success) {
        // Recharger la liste
        await loadPendingRequests();
        
        // Success message
        const actionText = action === 'approve' ? t('entityRequestManager.messages.approved') : t('entityRequestManager.messages.rejected');
        toast.success(`${t('entityRequestManager.messages.requestPrefix')} ${actionText} ${t('entityRequestManager.messages.successSuffix')}`);
      }

    } catch (error) {
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.error || t('entityRequestManager.errors.validation'));
    } finally {
      setProcessingId(null);
    }
  };

  const getRequestTypeLabel = (requestType) => {
    switch (requestType) {
      case 'join_existing':
        return `👥 ${t('entityRequestManager.requestTypes.joinOrganization')}`;
      case 'create_organization':
        return `🏗️ ${t('entityRequestManager.requestTypes.createOrganization')}`;
      default:
        return requestType;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'user':
        return `👤 ${t('entityRequestManager.roles.user')}`;
      case 'org-admin':
        return `👑 ${t('entityRequestManager.roles.admin')}`;
      default:
        return role;
    }
  };

  const canValidate = (request) => {
    if (user.role === 'super-admin') {
      return true; // Super admin peut tout valider
    }
    
    if (user.role === 'org-admin' && request.requestType === 'join_existing') {
      return request.organizationId?._id === user.organizationId; // Admin peut valider pour son org
    }
    
    return false;
  };

  if (loading) {
    return (
      <div className="request-manager">
        <div className="loading">{t('entityRequestManager.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="request-manager">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="request-manager">
      <div className="manager-header">
        <h2>🔔 {t('entityRequestManager.title')}</h2>
        <button onClick={loadPendingRequests} className="refresh-btn">
          🔄 {t('entityRequestManager.buttons.refresh')}
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="no-requests">
          <div className="no-requests-icon">✅</div>
          <h3>{t('entityRequestManager.noRequests.title')}</h3>
          <p>{t('entityRequestManager.noRequests.message')}</p>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map(request => (
            <RequestCard
              key={request._id}
              request={request}
              onValidate={handleValidation}
              canValidate={canValidate(request)}
              isProcessing={processingId === request._id}
              getRequestTypeLabel={getRequestTypeLabel}
              getRoleLabel={getRoleLabel}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const RequestCard = ({ 
  request, 
  onValidate, 
  canValidate, 
  isProcessing, 
  getRequestTypeLabel, 
  getRoleLabel,
  t
}) => {
  const [comment, setComment] = useState('');
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const handleAction = (action) => {
    if (action === 'reject') {
      setShowCommentBox(true);
      setPendingAction(action);
    } else {
      onValidate(request._id, action, comment);
    }
  };

  const handleCommentSubmit = () => {
    onValidate(request._id, pendingAction, comment);
    setShowCommentBox(false);
    setComment('');
    setPendingAction(null);
  };

  const handleCommentCancel = () => {
    setShowCommentBox(false);
    setComment('');
    setPendingAction(null);
  };

  return (
    <div className="request-card">
      <div className="request-header">
        <div className="request-type">
          {getRequestTypeLabel(request.requestType)}
        </div>
        <div className="request-date">
          {new Date(request.createdAt).toLocaleDateString('fr-FR')}
        </div>
      </div>

      <div className="request-content">
        <div className="user-info">
          <h4>👤 {request.userId?.firstName} {request.userId?.lastName}</h4>
          <p className="user-email">{request.userEmail}</p>
        </div>

        <div className="request-details">
          {request.requestType === 'join_existing' && (
            <>
              <div className="detail-item">
                <strong>{t('entityRequestManager.details.organization')} :</strong> {request.organizationId?.name}
              </div>
              <div className="detail-item">
                <strong>{t('entityRequestManager.details.requestedRole')} :</strong> {getRoleLabel(request.requestedRole)}
              </div>
            </>
          )}

          {request.requestType === 'create_organization' && (
            <>
              <div className="detail-item">
                <strong>{t('entityRequestManager.details.organizationName')} :</strong> {request.organizationName}
              </div>
              {request.organizationDescription && (
                <div className="detail-item">
                  <strong>{t('entityRequestManager.details.description')} :</strong> {request.organizationDescription}
                </div>
              )}
              <div className="detail-item">
                <strong>{t('entityRequestManager.details.role')} :</strong> {getRoleLabel('org-admin')} ({t('entityRequestManager.details.creator')})
              </div>
            </>
          )}
        </div>
      </div>

      {showCommentBox && (
        <div className="comment-box">
          <label htmlFor={`comment-${request._id}`}>
            {t('entityRequestManager.comment.label')} :
          </label>
          <textarea
            id={`comment-${request._id}`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('entityRequestManager.comment.placeholder')}
            rows="3"
          />
          <div className="comment-actions">
            <button onClick={handleCommentCancel} className="btn-cancel">
              {t('entityRequestManager.buttons.cancel')}
            </button>
            <button onClick={handleCommentSubmit} className="btn-reject">
              {t('entityRequestManager.buttons.reject')}
            </button>
          </div>
        </div>
      )}

      {canValidate && !showCommentBox && (
        <div className="request-actions">
          <button
            onClick={() => handleAction('approve')}
            disabled={isProcessing}
            className="btn-approve"
          >
            {isProcessing ? `⏳ ${t('entityRequestManager.buttons.processing')}` : `✅ ${t('entityRequestManager.buttons.approve')}`}
          </button>
          <button
            onClick={() => handleAction('reject')}
            disabled={isProcessing}
            className="btn-reject"
          >
            ❌ {t('entityRequestManager.buttons.reject')}
          </button>
        </div>
      )}

      {!canValidate && (
        <div className="no-permission">
          ⚠️ {t('entityRequestManager.noPermission')}
        </div>
      )}
    </div>
  );
};

export default EntityRequestManager;
